<script setup>
import { computed, onMounted, ref } from 'vue'
import { Stack, Typography } from '@aryeo/runway'
import { InformationCircleIcon, CheckCircleIcon, ExclamationIcon } from '@heroicons/vue/solid'
import { Icon } from '@aryeo/runway'
import useLocalStorage from '@/Hooks/Shared/useLocalStorage.js'

const props = defineProps({
  title: {
    type: String,
    required: false,
  },
  description: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    default: 'info',
  },
  hideIcon: {
    type: Boolean,
    default: false,
  },
  fullWidthDescription: {
    type: Boolean,
    default: false,
  },
  // A unique identifier to track whether the alert has been dismissed in localStorage.
  // Recommend prefixing with the page name, e.g. "dashboard.listings-backfilled"
  identifier: {
    type: String,
  },
  // Can this alert be dismissed? Note: requires an identifier also.
  isDismissible: {
    type: Boolean,
    default: false,
  },
})

const dismissed = ref(false)

const alertRole = computed(() => (props.type === 'danger' ? 'alert' : 'status'))

const { getItem, setItem } = useLocalStorage()

onMounted(() => {
  if (props.identifier && props.isDismissible && getItem(`dismissed.${props.identifier}`)) {
    dismissed.value = true
  }
})

const dismiss = () => {
  if (props.identifier && props.isDismissible) {
    dismissed.value = true
    setItem(`dismissed.${props.identifier}`, true)
  }
}

const alertSettings = () => {
  let icon = InformationCircleIcon
  let iconColor = 'text-info'

  switch (props.type) {
    case 'success':
      icon = CheckCircleIcon
      iconColor = 'text-success'
      break
    case 'danger':
      icon = ExclamationIcon
      iconColor = 'text-danger'
      break
    case 'warning':
      icon = ExclamationIcon
      iconColor = 'text-warning'
      break
    case 'disabled':
      icon = InformationCircleIcon
      iconColor = 'text-info'
      break
    default:
      break
  }

  return {
    icon,
    iconColor,
  }
}
</script>

<template>
  <div
    v-if="!dismissed"
    :role="alertRole"
    class="p-4 text-left rounded-xl bg-background-light border border-border-light"
  >
    <div class="flex">
      <div class="shrink-0">
        <component
          v-if="!hideIcon"
          :is="alertSettings().icon"
          class="w-6 h-6"
          :class="alertSettings().iconColor"
          aria-hidden="true"
        />
      </div>
      <div class="w-full" :class="{ 'ml-3': !hideIcon }">
        <Stack space="small">
          <Stack space="xsmall" v-if="title || description">
            <Typography variant="body" as="h3" v-if="title">{{ title }}</Typography>
            <Typography
              variant="body-sm"
              v-if="description"
              :class="fullWidthDescription ? 'w-full' : 'text-balance'"
              >{{ description }}</Typography
            >
          </Stack>
          <div v-if="$slots.default">
            <slot />
          </div>
        </Stack>
      </div>
      <div v-if="identifier && isDismissible">
        <a @click.prevent="dismiss" class="cursor-pointer">
          <Icon size="small" name="times" />
        </a>
      </div>
    </div>
  </div>
</template>
